// Time part (after "T" or a space) already carries a zone: Z/z or ±HH, ±HH:MM, ±HHMM.
const HAS_OFFSET = /(?:[Zz]|[+-]\d{2}(?::?\d{2})?)$/

/**
 * Parses a server timestamp to epoch ms. The API sends some timestamps as
 * naive ISO strings ("2026-10-21T06:42:35.396383") that are UTC; `new Date`
 * would read those as LOCAL time. A string whose time part has no Z/offset is
 * therefore parsed as UTC. Date-only strings (YYYY-MM-DD) are already read as
 * UTC by JS and pass through unchanged; explicit offsets are respected.
 *
 * @returns epoch ms, or null when missing/unparseable
 */
export function parseServerTime(iso: string | null | undefined): number | null {
  if (!iso) return null
  const sep = iso.search(/[T ]/)
  const needsZone = sep !== -1 && !HAS_OFFSET.test(iso.slice(sep + 1))
  const ms = new Date(needsZone ? `${iso}Z` : iso).getTime()
  return Number.isFinite(ms) ? ms : null
}

/** Formats whole seconds as "45s" / "4m" / "2h" / "3d" (largest unit, floored). */
function span(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`
  const m = Math.floor(totalSeconds / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

/**
 * Formats an ISO timestamp as a short relative-age string ("3s ago",
 * "12m ago", "4h ago", "2d ago"). Shared by BookCard, ConvictionStrip, and
 * the M3a book-detail panels — previously duplicated per-component.
 * Offset-less timestamps are read as UTC (see parseServerTime).
 *
 * @param iso timestamp to format, or null/undefined if unknown
 * @param fallback text to show when iso is missing/unparseable (default '—';
 *   ConvictionStrip passes 'no tick yet' for its tick-freshness readout)
 */
export function relativeTime(iso: string | null | undefined, fallback = '—'): string {
  const t = parseServerTime(iso)
  if (t === null) return fallback
  const ms = Date.now() - t
  if (ms < 0) return 'just now'
  return `${span(Math.floor(ms / 1000))} ago`
}

/**
 * Formats a future timestamp as a countdown ("in 45s", "in 4m", "in 2h",
 * "in 3d"), or "due" once the instant is now or past.
 *
 * @param iso timestamp to count down to, or null/undefined if unknown
 * @param fallback text to show when iso is missing/unparseable (default '—')
 */
export function untilTime(iso: string | null | undefined, fallback = '—'): string {
  const t = parseServerTime(iso)
  if (t === null) return fallback
  const ms = t - Date.now()
  if (ms <= 0) return 'due'
  return `in ${span(Math.floor(ms / 1000))}`
}
